import { AB_TEST_MAX_VARIANTS, AB_TEST_MIN_VARIANTS, AB_TEST_VARIANT_PATTERN, AbTestConfiguration, AbTestConfigurationInput } from '@pubstack/common/src/abTest'
import { Button } from '@pubstack/ui/components/Button'
import { Input } from '@pubstack/ui/components/Input'
import { FunctionComponent } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useAbTests, useCreateAbTest, useToggleAbTest } from '~/api/app/ab-test.api'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { ContentCard } from '~/components/ContentCard'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { useToast } from '~/components/Toast/useToasts'
import { ActivateAbTestModal } from '~/modules/settings/abTest/ActivateAbTestModal'
import { SETTINGS_NAV_CONFIG } from '~/utils/settings'

const DEFAULT_VALUES: AbTestConfigurationInput = {
  name: '',
  variants: Array.from({ length: AB_TEST_MIN_VARIANTS }, () => ({ variant: '' })),
}

export const AddAbTestPage: FunctionComponent = () => {
  const navigate = useNavigate()
  const modal = useGlobalModal()
  const toast = useToast()
  const { isOwner } = useScopePermissions()
  // undefined until the list is read, or when it could not be: the live test is then unknown
  const { data: abTests, isLoading: isLoadingAbTests } = useAbTests()
  const { mutateAsync: createAbTest, isPending: isCreating } = useCreateAbTest()
  const { mutateAsync: toggleAbTest, isPending: isToggling } = useToggleAbTest()
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AbTestConfigurationInput>({ defaultValues: DEFAULT_VALUES })
  const { fields, append, remove } = useFieldArray({ control, name: 'variants' })

  const navigateToView = (id: string) => navigate(`/settings/ab-test/view/${id}`)

  const activateAbTest = async (abTest: AbTestConfiguration) => {
    try {
      const activated = await toggleAbTest({ id: abTest.id })
      toast.success('A/B test activated.')
      navigateToView(activated.id)
    } catch (_e) {
      toast.alert(`Something went wrong when trying to activate ${abTest.name}`)
    }
  }

  const onSave = async ({ name, variants }: AbTestConfigurationInput) => {
    try {
      const created = await createAbTest({ name, variants: variants.map(({ variant }) => ({ variant: variant.trim() })) })
      if (!isOwner) {
        // enabling a test is an owner action: it stays saved, disabled
        toast.success('A/B test saved. Ask an owner of your scope to enable it.')
        navigateToView(created.id)
      } else if (abTests?.some((abTest) => abTest.active) ?? true) {
        // only one test runs per scope: ask before replacing the live one, or when the list could not be read; the test is saved either way
        modal.open(
          ActivateAbTestModal,
          { abTest: created, onToggle: () => activateAbTest(created), navigateToView: () => navigateToView(created.id) },
          { onAfterDefaultClose: () => navigateToView(created.id) }
        )
      } else {
        const activated = await toggleAbTest({ id: created.id })
        navigateToView(activated.id)
      }
    } catch (e) {
      // the api client rethrows the response body, `{ message }` for a handled backend error
      const message = e && typeof e === 'object' && 'message' in e && typeof e.message === 'string' ? e.message : undefined
      toast.alert(message ?? 'An error occurred while saving your A/B test, please try again later.')
    }
  }

  return (
    <form className="mx-auto my-5 flex max-w-[1000px] flex-col gap-5">
      <ContentCard color={SETTINGS_NAV_CONFIG.abTest.color} className="flex flex-col gap-6">
        <h2 className="m-0 border-b border-solid border-platinum pb-5 font-rubik text-xl leading-[30px] font-normal text-jet">Setup test</h2>
        <div className="flex flex-col gap-4">
          <h2>Identification</h2>
          <Input className="w-[304px]" error={errors?.name?.message} name="name" control={control} placeholder="Test name" rules={{ required: 'Name required' }} />
          <div>
            <h2 className="mt-[26px] mb-1">Variants</h2>
            Set from 2 to 4 variants
          </div>
          <div className="flex flex-col items-start gap-7">
            {fields.map((field, index) => (
              <section key={field.id} className="inline-flex items-center gap-3">
                <Input
                  className="w-[304px]"
                  error={errors?.variants?.[index]?.variant?.message}
                  placeholder="Variant name"
                  control={control}
                  name={`variants.${index}.variant`}
                  rules={{
                    required: 'Variant required',
                    pattern: { value: AB_TEST_VARIANT_PATTERN, message: 'Incorrect syntax' },
                  }}
                />
                {index >= AB_TEST_MIN_VARIANTS && <Button onClick={() => remove(index)} variant="tertiary" iconName="trash" trackingLabel="common-settings-ab-test-variant-delete" />}
              </section>
            ))}
            {fields.length < AB_TEST_MAX_VARIANTS && (
              <Button variant="secondary" iconName="add_circle" onClick={() => append({ variant: '' }, { shouldFocus: false })} trackingLabel="common-settings-ab-test-variant-new">
                Add variant
              </Button>
            )}
          </div>
          <div className="pt-2 text-right">
            <Button disabled={isLoadingAbTests || isCreating || isToggling} onClick={handleSubmit(onSave)} trackingLabel="common-settings-ab-test-save">
              Validate
            </Button>
          </div>
        </div>
      </ContentCard>
    </form>
  )
}
